import * as actions from "../actions/customers";

const customersInitialState = {
  customers: null,
  contracts: null,
  customerContractsUpload: null,
  customersListFilter: {
    searchTerm: null,
    tariff: null,
    additionalContracts: null,
    agreementVersion: null,
  },
  tariff: {
    operationalNetworks: null,
    tariffs: null,
    activeTariff: null,
    savingTariff: false,
    serverErrors: null,
  },
  tariffListFilter: {
    tariffNameOrPlanCode: null,
    network: null,
    contract: null,
    sortField: null,
  },
  activeCustomer: null,
};

const contractsReducer = (state = customersInitialState, action) => {
  switch (action.type) {
    case "RESET_STORE":
      return {
        ...customersInitialState,
      };
    case actions.CLEAR_CUSTOMER_CONTRACTS:
      return {
        ...customersInitialState,
      };
    case actions.UPDATE_CUSTOMER_CONTRACTS:
      return {
        ...state,
        customers: action.payload.customers,
        contracts: action.payload.contracts,
        requiresRefresh: false,
      };
    case actions.UPLOAD_CUSTOMER_CONTRACTS_FETCH_SIGNED_URL:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "FETCHING_SIGNED_URL",
        },
      };
    case actions.UPLOAD_CUSTOMER_CONTRACTS:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "UPLOAD_IN_PROGRESS",
          filename: action.payload.filename,
        },
      };
    case actions.UPLOAD_CUSTOMER_CONTRACTS_SUCCESS:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "UPLOAD_COMPLETE",
        },
      };
    case actions.APPLY_CUSTOMER_CHANGES_PROGRESS:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "APPLY_CUSTOMER_CHANGES_PROGRESS",
        },
      };
    case actions.UPLOAD_CUSTOMER_CONTRACTS_FAILURE:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "UPLOAD_FAILURE",
        },
      };
    case actions.UPDATE_UPLOAD_CUSTOMER_CONTRACTS_PROGRESS:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          // The CLIENT_UPLOAD response status simply means that were in the "uploading" phase of the
          // cycle. This is done entirely client side so the client has a better idea of the progress
          // than the server so we just ignore the server result for this.
          status:
            action.payload.status === "CLIENT_UPLOAD"
              ? state.customerContractsUpload.status
              : action.payload.status,
        },
      };
    case actions.CLEAR_UPLOAD_CUSTOMER_CONTRACTS:
      return {
        ...state,
        customerContractsUpload: null,
      };
    case actions.UPDATE_CUSTOMER_LIST_FILTER:
      return {
        ...state,
        customersListFilter: {
          ...state.customersListFilter,
          [action.payload.filterKey]: action.payload.filterValue,
        },
      };
    case actions.CLEAR_CUSTOMER_LIST_FILTER:
      return {
        ...state,
        customersListFilter: customersInitialState.customersListFilter,
      };
    case actions.UPDATE_TARIFF_LIST_FILTER:
      return {
        ...state,
        tariffListFilter: {
          ...state.tariffListFilter,
          [action.payload.filterKey]: action.payload.filterValue,
        },
      };
    case actions.CLEAR_TARIFF_LIST_FILTER:
      return {
        ...state,
        tariffListFilter: customersInitialState.tariffListFilter,
      };
    case actions.UPDATE_CUSTOMER_CONTRACTS_UPLOAD_CHANGES:
      const changeSet = action.payload.changeSet;
      const changes = getCustomerContractChanges(changeSet);

      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          changeSet: {
            ...changes,
          },
        },
      };
    case actions.UPDATE_OPERATIONAL_NETWORKS:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          operationalNetworks: action.payload.networks,
        },
      };
    case actions.UPDATE_CUSTOMER_DETAILS:
      return {
        ...state,
        activeCustomer: {
          ...(state.activeCustomer ? state.activeCustomer : {}),
          ...action.payload.customerDetails,
        },
      };
    case actions.CLEAR_CUSTOMER_DETAILS:
      return {
        ...state,
        activeCustomer: null,
      };
    case actions.UPDATE_CURRENT_CUSTOMER_TARIFF:
      return {
        ...state,
        activeCustomer: {
          ...(state.activeCustomer ? state.activeCustomer : {}),
          currentTariff: action.payload.tariff,
        },
      };
    case actions.UPDATE_ALL_TARIFFS:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          tariffs: action.payload.tariffs,
        },
      };
    case actions.UPDATE_TARIFF:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          activeTariff: action.payload.tariff,
        },
      };
    case actions.CLEAR_ACTIVE_TARIFF:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          activeTariff: null,
        },
      };
    case actions.APPLY_CUSTOMER_CHANGES:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "CHANGES_APPLIED",
        },
      };
    case actions.APPLY_CUSTOMER_CHANGES_SUCCESS:
      const changeAppliedSet = action.payload.changeSet;
      const appliedChanges = getAppliedContractChanges(changeAppliedSet);

      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          changeSet: {
            ...appliedChanges,
          },
          status: "CHANGES_APPLIED",
        },
        requiresRefresh: true,
      };
    case actions.APPLY_CUSTOMER_CHANGES_ERROR:
      return {
        ...state,
        customerContractsUpload: {
          ...state.customerContractsUpload,
          status: "APPLY_CUSTOMER_CHANGES_ERROR",
        },
        requiresRefresh: true,
      };
    case actions.FETCH_SAVE_NEW_TARIFF:
    case actions.FETCH_SAVE_EXISTING_TARIFF:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          savingTariff: true,
          serverErrors: null,
        },
      };
    case actions.UPDATE_SAVE_NEW_TARIFF:
    case actions.UPDATE_SAVE_EXISTING_TARIFF:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          activeTariff: action.payload.tariff,
          savingTariff: false,
          serverErrors: action.payload.tariffErrors,
        },
      };
    case actions.ERROR_SAVE_NEW_TARIFF:
    case actions.ERROR_SAVE_EXISTING_TARIFF:
      return {
        ...state,
        tariff: {
          ...state.tariff,
          savingTariff: false,
          serverErrors: null,
        },
      };
    default:
      return state;
  }
};

function getCustomerContractChanges(changeSet) {
  // Process the change set into a lists of change type.
  let added = [];
  let changed = [];
  let removed = [];
  let nochange = [];
  let unknown = [];
  let error = [];
  for (let i in changeSet.customers) {
    const customer = changeSet.customers[i];
    let totalCount = 0;
    let enableCount = 0;
    let disableCount = 0;
    let modifyCount = 0;

    if (customer.status === "NOT_FOUND") {
      // Reset the contract status for any unknown customers to null as we don't really know
      // its change status.
      for (let i in customer.contracts) {
        customer.contracts[i].change = null;
      }

      unknown.push(customer);
      continue;
    }

    for (let i in customer.contracts) {
      const contract = customer.contracts[i];
      if (contract.status === "UNSUCCESSFUL") {
        error.push(customer);
        continue;
      }
      if (contract.change === "enable") {
        enableCount++;
      }

      if (contract.change === "disable") {
        disableCount++;
      }

      if (contract.change === "modify") {
        modifyCount++;
      }

      totalCount++;
    }

    let tariff = customer.tariff;

    if (tariff) {
      if (tariff.status === "UNSUCCESSFUL") {
        error.push(customer);
        continue;
      }
      totalCount++;
      if (tariff.change === "enable") {
        enableCount++;
      }
      if (tariff.change === "disable") {
        disableCount++;
      }
      if (tariff.change === "modify") {
        modifyCount++;
      }
    }

    // If a customer has no contracts in the changes then it must mean that its a customer
    // that e don't yet know about and they have uploaded no contracts/plans data for them
    // in such a scenario we should just ignore them.
    if (totalCount === 0) {
      continue;
    }

    // If all the contracts are disabled for this customer then we are removing the customer.
    if (disableCount === totalCount) {
      removed.push(customer);
      continue;
    }

    // If all the contracts are being enabled then we are adding the customer.
    if (enableCount === totalCount) {
      added.push(customer);
      continue;
    }

    // If all the contracts are being modified then we are updating the customer.
    if (modifyCount === totalCount) {
      changed.push(customer);
      continue;
    }

    if (enableCount === 0 && disableCount === 0 && modifyCount === 0) {
      nochange.push(customer);
      continue;
    }

    changed.push(customer);
  }

  return {
    added: added,
    changed: changed,
    nochange: nochange,
    removed: removed,
    unknown: unknown,
    error: error,
  };
}

function getAppliedContractChanges(changeAppliedSet) {
  // Process the change set into a lists of change type.
  let success = [];
  let partial = [];
  let failed = [];
  let unknown = [];
  let nochange = [];
  for (let i in changeAppliedSet.customers) {
    const customer = changeAppliedSet.customers[i];
    let totalCount = 0;
    let successCount = 0;
    let failCount = 0;

    if (customer.status === "NOT_FOUND") {
      // Reset the contract status for any unknown customers to null as we don't really know
      // its change status.
      for (let i in customer.contracts) {
        customer.contracts[i].change = null;
      }

      unknown.push(customer);
      continue;
    }

    for (let i in customer.contracts) {
      const contract = customer.contracts[i];
      if (contract.change !== null) {
        if (contract.status === "SUCCESSFUL") {
          successCount++;
        }

        if (contract.status === "UNSUCCESSFUL") {
          failCount++;
        }
      }

      totalCount++;
    }

    let tariff = customer.tariff;
    if (tariff) {
      totalCount++;
      if (tariff.change !== null) {
        if (tariff.status === "SUCCESSFUL") {
          successCount++;
        }
        if (tariff.status === "UNSUCCESSFUL") {
          failCount++;
        }
      }
    }

    // If a customer has no contracts in the changes then it must mean that its a customer
    // that e don't yet know about and they have uploaded no contracts/plans data for them
    // in such a scenario we should just ignore them.
    if (totalCount === 0) {
      continue;
    }

    // If all the contracts are disabled for this customer then we are removing the customer.
    if (failCount > 0 && successCount === 0) {
      failed.push(customer);
      continue;
    }

    // If all the contracts are being enabled then we are adding the customer.
    if (successCount > 0 && failCount === 0) {
      success.push(customer);
      continue;
    }

    // If all the contracts are being modified then we are updating the customer.
    if (successCount > 0 && failCount > 0) {
      partial.push(customer);
      continue;
    }

    if (successCount === 0 && failCount === 0) {
      nochange.push(customer);
      continue;
    }
  }

  return {
    success: success,
    partial: partial,
    nochange: nochange,
    failed: failed,
    unknown: unknown,
  };
}

export default contractsReducer;
